import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { AuthModal } from '@/components/auth/AuthModal';
import { supabase } from '@/lib/supabase/client';
import type { Experience } from '@/types';

interface BookingButtonProps {
  experience: Experience;
  quantity: number;
  date: string;
  className?: string;
}

export function BookingButton({ experience, quantity, date, className }: BookingButtonProps) {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Check if user is the creator of this experience
  const isCreator = user?.id === experience.creator_id;

  const handleBooking = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!user) {
      setShowAuthModal(true);
      return;
    }

    // Prevent creators from booking their own experiences
    if (isCreator) {
      setError("You cannot book your own experience");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      if (experience.booking_type === 'instant') {
        // Create checkout session for instant bookings
        const response = await fetch('/.netlify/functions/create-checkout-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            experienceId: experience.id,
            quantity,
            date,
            userId: user.id
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create checkout session');
        }

        const { sessionId } = await response.json();

        const stripe = await import('@stripe/stripe-js')
          .then(m => m.loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY));
        
        if (!stripe) {
          throw new Error('Failed to load payment system');
        }
        
        const result = await stripe.redirectToCheckout({
          sessionId
        });
        
        if (result.error) {
          throw new Error(result.error.message);
        }
      } else {
        // Create booking request for request-to-join
        const { error: bookingError } = await supabase
          .from('bookings')
          .insert({
            experience_id: experience.id,
            user_id: user.id,
            participant_count: quantity,
            status: 'pending',
            payment_status: 'pending',
            total_amount: experience.price * quantity,
            special_requests: ''
          });

        if (bookingError) {
          throw bookingError;
        }

        navigate('/bookings?success=true&type=request');
      }
    } catch (error) {
      console.error('Booking error:', error);
      setError(error instanceof Error ? error.message : 'Failed to process booking');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="space-y-2">
        {isCreator ? (
          <Button
            className={className}
            variant="outline"
            disabled={true}
          >
            Your Experience
          </Button>
        ) : (
          <Button
            className={className}
            onClick={handleBooking}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 
              experience.booking_type === 'instant' ? 
                `Book for $${experience.price * quantity}` : 
                'Request to Join'}
          </Button>
        )}

        {error && (
          <p className="text-sm text-red-500 text-center">{error}</p>
        )}
      </div>

      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="sign-up"
        redirectTo={`/experiences/${experience.id}/book`}
      />
    </>
  );
}